/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePipe } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  CaseDetails,
  CaseReferenceDetails,
  CommonNote,
  DisplayableFieldResponse,
  DisplayableFieldValue,
  IP,
  MatterDetails,
  TrademarkDetails,
  WorkflowDefinitionDO,
  WorkflowDO,
  WorkflowTaskDefinitionDO,
  WorkflowTaskDO,
  WsAccountDetails,
  WsFile,
} from '@model/ftf-api.model';
import { getFileNameFromPath } from '@utils/file-utils';
import { Subject, takeUntil } from 'rxjs';

import { AccountService } from './account.service';
import { LoggerService } from './logger.service';

// #region Type Definitions
interface FieldDetails {
  label?: string;
  labelfield?: string;
  convert?: (this: DataTransformService, value: any, record: any) => string | null;
  displayabledetails?: (this: DataTransformService, value: any) => DisplayableFieldResponse;
}
interface CommonFieldDetails {
  [key: string]: FieldDetails;
}
type ReferenceNumeric = '1' | '2' | '3';
interface CaseDetailsMap {
  Patent: { [key in keyof CaseDetails]: FieldDetails };
  Trademark: { [key in keyof TrademarkDetails]: FieldDetails };
  Matter: { [key in keyof MatterDetails]: FieldDetails };
}
type MappedCaseDetails<T extends keyof CaseDetailsMap> = {
  [K in T]: CaseDetailsMap[K];
};
interface ApiFieldLabels {
  casedetails: MappedCaseDetails<IP.TrifTypeEnum>;
  reference: { [key in ReferenceNumeric]: { [key in keyof CaseReferenceDetails]: FieldDetails } };
  trifoldfile: { [key in keyof WsFile]: FieldDetails };
  note: { [key in keyof CommonNote]: FieldDetails };
  workflow: { [key in keyof WorkflowDO]: FieldDetails };
  workflowtask: { [key in keyof WorkflowTaskDO]: FieldDetails };
  workflowdef: { [key in keyof WorkflowDefinitionDO]: FieldDetails };
  workflowtaskdef: { [key in keyof WorkflowTaskDefinitionDO]: FieldDetails };
}
// #endregion
const REFERENCE_NAME_TO_NUMBER: { [key: string]: ReferenceNumeric } = {
  'Non-Patent Reference': '1',
  'Foreign Patent': '2',
  'US Patent': '3',
};
const convertDate = function (this: DataTransformService, v: any) {
  return v ? this.helper.datePipe.transform(v, 'dd-MMM-yyyy') : '';
};
export const sortByKeys = function (resp: DisplayableFieldResponse) {
  resp.displayable = resp.displayable.sort((a, b) => a.key.localeCompare(b.key));
  return resp;
};
/**
 * API_FIELD_LABELS is a mapping of field names to their display labels and conversion functions.
 *
 * Its purpose is to convert API field names to human-readable labels and any conversion functions
 * to prep values for display
 */
const API_FIELD_LABELS: ApiFieldLabels = {
  casedetails: {
    Patent: {
      title: { label: 'Case Title' },
      country: { label: 'Country' },
      caseType: { label: 'Case Type' },
      applicationType: { label: 'Application Type' },
      jurisdiction: { label: 'Filing Jurisdiction' },
      caseSuffix: { label: 'Case Suffix', labelfield: 'casebibliographic.CaseSuffix' },
      caseStatus: { label: 'Status' },
      serialNumber: { label: 'Serial No.' },
      fileDate: { label: 'Filing Date' },
      patentNumber: { label: 'Patent Number' },
      issueDate: { label: 'Issue Date' },
      primaryCaseNm: { label: 'Case Number', labelfield: 'casebibliographic.CaseNumberFieldTitle' },
      createDate: { label: 'Create Date' },
      createByUser: { label: 'Created By' },
      modifiedDate: { label: 'Modified Date' },
      modifyByUser: { label: 'Modify By' },
      caseFamilyNm: { label: 'Case Family', labelfield: 'casebibliographic.CaseFamily' },
      groups: { label: 'Group', labelfield: 'casecategory.Group' },
      products: { label: 'Product', labelfield: 'casecategory.Product' },
      technologies: { label: 'Technology Cluster', labelfield: 'casecategory.TechnologyCluster' },
      inventors: { label: 'Inventors' },
      attorneys: { label: 'In-House Attorney', labelfield: 'caseprosecution.In-HouseAttorney' },
      assignees: { label: 'Assignee' },
      priorityDate: { label: 'Priority Date' },
      genericField1: { label: 'Generic Field 1', labelfield: 'casebibliographic.GenericField1' },
      genericField2: { label: 'Generic Field 2', labelfield: 'casebibliographic.GenericField2' },
      genericField3: { label: 'Generic Field 3', labelfield: 'casebibliographic.GenericField3' },
      genericField4: { label: 'Generic Field 4', labelfield: 'casebibliographic.GenericField4' },
      genericField5: { label: 'Generic Field 5', labelfield: 'casebibliographic.GenericField5' },
      genericField6: { label: 'Generic Field 6', labelfield: 'casebibliographic.GenericField6' },
      publicationNumber: { label: 'Publication Number' },
      publicationDates: {
        label: 'Publication Date',
        convert: function (v: any) {
          return v ? v.join(', ') : '';
        },
      },
      exportControlStatus: {
        label: 'Export Control Status',
        convert: function (v: any) {
          return v === 'Y' ? 'Yes' : 'No';
        },
      },
      caseUrl: { label: 'Url' },
    },
    Trademark: {
      assignee: { label: 'Assignee' },
      attorney: { label: 'Attorney' },
      caseFamily: { label: 'Case Family', labelfield: 'trademarkbibliographic.CaseFamily' },
      catGenericField1: { label: 'Cat Generic Field 1' },
      goodAndServicesClasses: { label: 'Class' },
      clientName: { label: 'Client Name', labelfield: 'trademarkbibliographic.ClientName' },
      clientReference: { label: 'Client Reference', labelfield: 'trademarkbibliographic.ClientReference' },
      country: { label: 'Country' },
      createByUser: { label: 'Create By' },
      createDate: { label: 'Create Date' },
      // depictionMark: { label: 'Depiction of Mark' },
      markDrawingCode: { label: 'Mark Drawing Code' },
      expireDate: { label: 'Expire Date' },
      exportControlStatus: {
        label: 'Export Control Status',
        convert: function (v: any) {
          return v === 'Y' ? 'Yes' : 'No';
        },
      },
      filingBasis: { label: 'Filing Basis' },
      filingDate: { label: 'Filing Date' },
      filingJurisdicton: { label: 'Filing Jurisdicton' },
      firstUseCommerce: { label: 'First Use in Commerce' },
      firstUseDate: { label: 'First Use Date' },
      genericField1: { label: 'Generic Field 1', labelfield: 'trademarkbibliographic.GenericField1' },
      genericField2: { label: 'Generic Field 2', labelfield: 'trademarkbibliographic.GenericField2' },
      genericField3: { label: 'Generic Field 3', labelfield: 'trademarkbibliographic.GenericField3' },
      genericField4: { label: 'Generic Field 4', labelfield: 'trademarkbibliographic.GenericField4' },
      genericField5: { label: 'Generic Field 5', labelfield: 'trademarkbibliographic.GenericField5' },
      genericField6: { label: 'Generic Field 6', labelfield: 'trademarkbibliographic.GenericField6' },
      lastRenewalDate: { label: 'Last Renewal Date' },
      modifiedDate: { label: 'Modified Date' },
      modifyByUser: { label: 'Modified By' },
      nextRenewalDate: { label: 'Next Renewal Date' },
      companyCaseNm: { label: 'Case Number', labelfield: 'trademarkbibliographic.CaseNumberFieldTitle' },
      proOutsideDocketNm: { label: 'Outside Counsel Docket #' },
      outsideAttorney: { label: 'Outside Counsel', labelfield: 'caseprosecution.OutsideCounsel' },
      ownerName: { label: 'Owner Name' },
      priorityDate: { label: 'Priority Date' },
      priorityNumber: { label: 'Priority Number' },
      priorityCountry: { label: 'Priority Country' },
      group: { label: 'Group', labelfield: 'casecategory.Group' },
      products: { label: 'Product', labelfield: 'casecategory.Product' },
      technologies: { label: 'Technology Cluster', labelfield: 'casecategory.TechnologyCluster' },
      registrationDate: { label: 'Registration Date' },
      registrationNumber: { label: 'Registration Number' },
      regType: { label: 'Mark Type' },
      serialNumber: { label: 'Serial No.' },
      statusDate: { label: 'Status Date' },
      status: { label: 'Status' },
      standardCharacterClaim: {
        label: 'Standard Character Claim',
        convert: function (v: any) {
          return v === 'Y' ? 'Yes' : 'No';
        },
      },
      //subStatusDate: { label: 'Sub Status Date' },
      subStatus: { label: 'Application Type' },
      caseSuffix: { label: 'Case Suffix', labelfield: 'trademarkbibliographic.CaseSuffix' },
      trademarkNm: { label: 'Trademark' },
      caseType: { label: 'Case Type' },
    },
    Matter: {
      attorneys: { label: 'In-House Attorney', labelfield: 'caseprosecution.In-HouseAttorney' },
      catGenericField1: { label: 'Category Generic Field 1', labelfield: 'casecategory.GenericField1' },
      clientName: { label: 'Client Name', labelfield: 'matterbibliographic.ClientName' },
      clientReference: { label: 'Client Reference', labelfield: 'matterbibliographic.ClientReference' },
      country: { label: 'Country' },
      createByUser: { label: 'Create By' },
      createDate: { label: 'Create Date' },
      expireDate: { label: 'Expire Date' },
      exportControlStatus: {
        label: 'Export Control Status',
        convert: function (v: any) {
          return v === 'Y' ? 'Yes' : 'No';
        },
      },
      filingDate: { label: 'Filing Date' },
      genericDate1: { label: 'Generic Date 1', labelfield: 'matterbibliographic.GenericDate1' },
      genericDate2: { label: 'Generic Date 2', labelfield: 'matterbibliographic.GenericDate2' },
      genericDate3: { label: 'Generic Date 3', labelfield: 'matterbibliographic.GenericDate3' },
      genericDate4: { label: 'Generic Date 4', labelfield: 'matterbibliographic.GenericDate4' },
      genericDate5: { label: 'Generic Date 5', labelfield: 'matterbibliographic.GenericDate5' },
      genericDate6: { label: 'Generic Date 6', labelfield: 'matterbibliographic.GenericDate6' },
      genericField1: { label: 'Generic Field 1', labelfield: 'matterbibliographic.GenericField1' },
      genericField2: { label: 'Generic Field 2', labelfield: 'matterbibliographic.GenericField2' },
      genericField3: { label: 'Generic Field 3', labelfield: 'matterbibliographic.GenericField3' },
      genericField4: { label: 'Generic Field 4', labelfield: 'matterbibliographic.GenericField4' },
      genericField5: { label: 'Generic Field 5', labelfield: 'matterbibliographic.GenericField5' },
      genericField6: { label: 'Generic Field 6', labelfield: 'matterbibliographic.GenericField6' },
      matterFamilyNm: { label: 'Other Matter Family', labelfield: 'matterbibliographic.MatterFamily' },
      matterNm: { label: 'Other Matter Number', labelfield: 'matterbibliographic.MatterNumber' },
      matterSuffix: { label: 'Other Matter Suffix' },
      matterTitle: { label: 'Other Matter' },
      matterType: { label: 'Other Matter Type' },
      modifiedByUser: { label: 'Modified By' },
      modifiedDate: { label: 'Modified Date' },
      outsideAttorneys: { label: 'Outside Counsel', labelfield: 'caseprosecution.OutsideCounsel' },
      outsideCounselDocketNm: { label: 'Outside Counsel Docket #', labelfield: 'caseprosecution.OutsideCounselDocketNumber' },
      ownerName: { label: 'Owner' },
      priorityCountry: { label: 'Priority Country' },
      priorityDate: { label: 'Priority Date' },
      priorityNumber: { label: 'Priority Number' },
      groups: { label: 'Group', labelfield: 'casecategory.Group' },
      products: { label: 'Product', labelfield: 'casecategory.Product' },
      technologies: { label: 'Technology Cluster', labelfield: 'casecategory.TechnologyCluster' },
      registrationDate: { label: 'Registration Date' },
      registrationNumber: { label: 'Registration Number' },
      serialNumber: { label: 'Serial No.' },
      statusDate: { label: 'Status Date' },
      status: { label: 'Status' },
    },
  },
  trifoldfile: {
    assignDate: { label: 'Assigned Date' },
    completeDate: { label: 'Complete Date' },
    currentDueDate: { label: 'Due Date' },
    docSubtype: { label: 'Document Sub-Type' },
    docType: { label: 'Document Type' },
    documentDate: { label: 'Document Date' },
    fileName: { label: 'File Name' },
    filePriority: { label: 'Document Priority' },
    fileSize: { label: 'File Size' },
    genericField1: { label: 'Doc Gen 1', labelfield: 'documents.DocGen1' },
    genericField2: { label: 'Doc Gen 2', labelfield: 'documents.DocGen1' },
    genericField3: { label: 'Doc Gen 3', labelfield: 'documents.DocGen1' },
    genericField4: { label: 'Doc Gen 4', labelfield: 'documents.DocGen1' },
    hasAttachments: {
      label: 'Has Attachments',
      convert: function (v: any) {
        return v === 'Y' ? 'Yes' : 'No';
      },
    },
    receivedDateEpochTime: { label: 'Received Date', convert: convertDate },
    status: { label: 'Status' },
    technicalDocument: {
      label: 'Technical Document',
      convert: function (v: any) {
        return v === 'Y' ? 'Yes' : 'No';
      },
    },
    title: { label: 'Title' },
    usersByAssignBy: { label: 'Assigned By' },
    usersByAssignTo: { label: 'Assigned To' },
  },
  note: {
    createByUser: { label: 'Create By' },
    createDate: { label: 'Create Date', convert: convertDate },
    notes: { label: 'Note' },
  },
  reference: {
    // npl
    '1': {
      referenceTypeDisplay: { label: 'Reference Type' },
      titleDisplay: { label: 'Reference Name' },
      countryCode: { label: 'Country Where Published' },
      createdByUser: { label: 'Uploaded By' },
      articleTitle: { label: 'Title of Article' },
      itemTitle: { label: 'Title of Item' },
      fileName: {
        label: 'Reference Document File Name',
        convert: function (v: any) {
          return /placeHolder/.test(v) ? '' : getFileNameFromPath(v);
        },
      },
      author: { label: 'Name of Author' },
      publisher: { label: 'Publisher' },
      articleDate: { label: 'NPL Date' },
      volumeNumber: { label: 'Volume-Issue Number' },
      pages: { label: 'Pages' },
      customCitation: {
        label: 'Custom Citation',
        convert: function (v: any, r: any) {
          return r.useCustomCitation ? v || '' : '';
        },
      },
    },
    // foreign
    '2': {
      referenceTypeDisplay: { label: 'Reference Type' },
      titleDisplay: { label: 'Reference Name' },
      countryCode: { label: 'Country Code' },
      createdByUser: { label: 'Uploaded By' },
      kindCode: { label: 'Kind Code' },
      applicant: { label: 'Patentee or Applicant' },
      applicantNumber: { label: 'Foreign Document Number' },
      issueDate: { label: 'Issue Date' },
      pages: { label: 'Pages' },
      inventors: { label: 'Inventor' },
      assignee: { label: 'Assignee' },
      patentNumber: { label: 'Patent Number' },
      publicationNumber: { label: 'Publication Number' },
      publicationDate: { label: 'Publication Date' },
      fileName: {
        label: 'Reference Document File Name',
        convert: function (v: any) {
          return /placeHolder/.test(v) ? '' : getFileNameFromPath(v);
        },
      },
    },
    // us patent
    '3': {
      referenceTypeDisplay: { label: 'Reference Type' },
      titleDisplay: { label: 'Reference Name' },
      countryCode: { label: 'Country Code' },
      createdByUser: { label: 'Uploaded By' },
      patentNumber: { label: 'Patent Number' },
      kindCode: { label: 'Kind Code' },
      issueDate: { label: 'Issue Date' },
      applicant: { label: 'Patentee or Applicant' },
      pages: { label: 'Pages' },
      inventors: { label: 'Inventor' },
      assignee: { label: 'Assignee' },
      publicationNumber: { label: 'Publication Number' },
      publicationDate: { label: 'Publication Date' },
      fileName: {
        label: 'Reference Document File Name',
        convert: function (v: any) {
          return /placeHolder/.test(v) ? '' : getFileNameFromPath(v);
        },
      },
    },
  },
  workflow: {
    definition: {
      displayabledetails: function (v: any) {
        return this.displayableDetails(API_FIELD_LABELS.workflowdef, v);
      },
    },
    instructions: { label: 'Instructions' },
    dueDate: { label: 'Due Date', convert: convertDate },
    urgent: {
      label: 'Urgent',
      convert: function (v: any) {
        return v ? 'Yes' : '';
      },
    },
    currentTask: {
      displayabledetails: function (v: any) {
        return this.displayableDetails(API_FIELD_LABELS.workflowtask, v);
      },
    },
    caseNumber: { label: 'Case Number' },
  },
  workflowtask: {
    state: {
      label: 'Status',
      convert: function (v: any) {
        return v.display;
      },
    },
    definition: {
      displayabledetails: function (v: any) {
        return this.displayableDetails(API_FIELD_LABELS.workflowtaskdef, v);
      },
    },
    dueDate: { label: 'Task Due Date', convert: convertDate },
    priority: { label: 'Priority' },
    assignedTo: {
      label: 'Assigned To',
      convert: function (v: any) {
        return v ? v.firstName + ' ' + v.lastName : '';
      },
    },
    reviewLoop: {
      label: 'Review Loop Step',
      convert: function (v: any) {
        return v ? 'Yes' : '';
      },
    },
  },
  workflowdef: {
    name: { label: 'Workflow' },
    type: { label: 'Type' },
  },
  workflowtaskdef: {
    name: { label: 'Task' },
    step: { label: 'Step' },
  },
};

/**
 * Service responsible for transforming data for display purposes.
 *
 * This service utilizes the API_FIELD_LABELS configuration to map API field names
 * to user-friendly labels for display. The transformation includes formatting dates
 * and other account-specific details using the provided DatePipe and AccountService.
 */
@Injectable({ providedIn: 'root' })
export class DataTransformService implements OnDestroy {
  private accountService = inject(AccountService);
  private datePipe = inject(DatePipe);
  private loggerService = inject(LoggerService);

  private $ngDestroyed = new Subject<void>();

  helper: { config: WsAccountDetails | null; datePipe: DatePipe };

  constructor() {
    this.helper = {
      config: this.accountService.accountDetails,
      datePipe: this.datePipe,
    };
    // ensure account details get set upon load
    this.accountService.isAccountDetailsLoaded$.pipe(takeUntil(this.$ngDestroyed)).subscribe((loaded) => {
      if (loaded) {
        this.helper.config = this.accountService.accountDetails;
      }
    });
  }

  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  displayableDetails(fieldDefs: CommonFieldDetails, rec: any): DisplayableFieldResponse {
    const data: DisplayableFieldValue[] = [];

    for (const key in rec) {
      let value = rec[key];
      const def = fieldDefs[key];

      if (!def) {
        // skip fields that aren't defined
        continue;
      }

      // potentially nested defs objects within mapping
      if (def.displayabledetails && value) {
        const recs = def.displayabledetails.apply(this, [value]);
        recs.displayable.forEach((d) => data.push(d));
        continue;
      }

      let { label } = def;
      if (def.labelfield) {
        // allow custom field definition in
        label = this.helper.config?.customFields![def.labelfield] || label;
      }

      value = value != null ? (def.convert ? def.convert.apply(this, [value, rec]) : value) : '';
      if (label) {
        data.push({ key: label, value: value, field: key });
      } else {
        this.loggerService.warn(`No label for field ${key}`);
      }
    }

    return { displayable: data, raw: rec };
  }

  displayableFileDetails(file: WsFile) {
    return this.displayableDetails(API_FIELD_LABELS.trifoldfile, file);
  }

  displayableRefDetails(ref: CaseReferenceDetails) {
    const refTypeNumber = REFERENCE_NAME_TO_NUMBER[ref.referenceTypeDisplay!];
    return this.displayableDetails(API_FIELD_LABELS.reference[refTypeNumber], ref);
  }

  displayableCaseDetails(casetype: IP.TrifTypeEnum, rec: CaseDetails | TrademarkDetails | MatterDetails) {
    return sortByKeys(this.displayableDetails(API_FIELD_LABELS.casedetails[casetype], rec));
  }

  displayableNote(note: CommonNote) {
    return this.displayableDetails(API_FIELD_LABELS.note, note);
  }

  displayableNotes(notesRec: { notes: CommonNote[]; number: number }) {
    const newRec: { notes: DisplayableFieldResponse[]; number: number } = {
      number: notesRec.number,
      notes: [],
    };
    notesRec.notes.forEach((cur) => newRec.notes.push(this.displayableNote(cur)));
    return newRec;
  }

  displayableWorkflowTask(task: WorkflowTaskDO) {
    return this.displayableDetails(API_FIELD_LABELS.workflowtask, task);
  }

  displayableWorkflow(wf: WorkflowDO) {
    return this.displayableDetails(API_FIELD_LABELS.workflow, wf);
  }
}
